// import { uniq } from "lodash";
import { LatLng, distanceTo } from "geolocation-utils";

import { api } from "api";
import { Utils } from "utils";
import { IPlace, Prettify, Iterable } from "types";
import {
  Road,
  Circle,
  // SameRoads,
  RouteWayponts,
  BestBikingRoad,
  BestBikingRoads,
} from "services";

interface Props {
  avoid: string[];
  route: LatLng[];
  countries: string[];
  routeWaypoints: RouteWayponts;
  waypointsCountLimit: number;
}

export const buildBestBikingRoadsRoute = async (props: Prettify<Props>) => {
  const { convertToPoints, convertToLatLng } = Utils.Graphhopper;
  const { countries, routeWaypoints, route, avoid, waypointsCountLimit } =
    props;

  const roads = new BestBikingRoads(countries);
  await roads.fetch();
  roads.filterDisabled();

  const places = routeWaypoints.places;
  const waypoints = routeWaypoints.waypoints;

  let updatedWaypoints: LatLng[] = [];
  let updatedPlaces: (Iterable<IPlace> | undefined)[] = [];

  let croppedWaypointsFrom: LatLng[] = [];
  let usedBestBikingRoadsIDs: string[] = [];
  let selectedBestBikingRoads: BestBikingRoad[] = [];

  let selectedRoadsCoordinates: LatLng[][] = [];

  for (const waypointIndex in waypoints) {
    const index = Number(waypointIndex);

    const currentPlace = places[index];
    const currentWaypoint = waypoints[index];

    const isCropped =
      croppedWaypointsFrom.filter(
        ({ lat, lng }) =>
          lat === currentWaypoint.lat && lng === currentWaypoint.lng
      ).length > 0;

    if (isCropped) {
      continue;
    }

    if (index === waypoints.length - 1) {
      updatedWaypoints.push(currentWaypoint);
      updatedPlaces.push(currentPlace);
      continue;
    }

    const radius = 10; // in kms
    const circle = new Circle(currentWaypoint, radius * 1000);

    let includedBestBikingRoads = roads.all.filter((item) => {
      if (item.instance.disabled) {
        return false;
      }

      if (usedBestBikingRoadsIDs.includes(item.id)) {
        return false;
      }

      const isStartInside = circle.isInside(item.coordinates[0]);
      const isFinishInside = circle.isInside(
        item.coordinates[item.coordinates.length - 1]
      );

      const coordsWithDirection = item.getDirection(currentWaypoint);
      const bestBikingRoad = Road.fromLatLng(coordsWithDirection);

      const { indexes } = bestBikingRoad.findClosestPointsOn(route);
      const [startIndex, finishIndex] = indexes;
      const slice = route.slice(startIndex, finishIndex);
      const road = Road.fromLatLng(slice);
      const cropped = waypoints.filter((i) => road.include(i));

      if (cropped.length >= 4) {
        return false;
      }

      return isStartInside || isFinishInside;
    });

    includedBestBikingRoads.forEach((item) =>
      usedBestBikingRoadsIDs.push(item.id)
    );

    const croppedWaypoints = includedBestBikingRoads.map((bestBikingRoad) => {
      const { indexes } = bestBikingRoad.findClosestPointsOn(route);
      const [startIndex, finishIndex] = indexes;
      const slice = route.slice(startIndex, finishIndex);
      const road = Road.fromLatLng(slice);
      const cropped = waypoints.filter((item) => road.include(item));
      return cropped;
    });

    const nextWaypoints = includedBestBikingRoads.map((item) => {
      const coordsWithDirection = item.getDirection(currentWaypoint);
      const bestBikingRoad = Road.fromLatLng(coordsWithDirection);

      const { indexes } = bestBikingRoad.findClosestPointsOn(route);

      const finishIndex = indexes[1];
      const slice = route.slice(finishIndex, route.length);

      const road = Road.fromLatLng(slice);
      const waypointIndex = waypoints.findIndex((item) => road.include(item));
      const waypointPlace = places.at(waypointIndex);

      return {
        place: waypointPlace,
        waypoint: waypoints[waypointIndex],
      };
    });

    const placesAlong = includedBestBikingRoads.map((item, index) => {
      const coordsWithDirection = item.getDirection(currentWaypoint);
      const bestBikingRoad = Road.fromLatLng(coordsWithDirection);

      const searchRadius = 1; // km;
      const coordsAlongTheRoad = bestBikingRoad.coordinatesAlong(
        routeWaypoints.availablePlaces.map((i) => ({
          lat: i.latitude,
          lng: i.longitude,
        })),
        searchRadius,
        [0, 0]
      );

      const alongPlaces = routeWaypoints.availablePlaces.filter((item) => {
        const isAlong = coordsAlongTheRoad.filter((coord) => {
          const location = Utils.Places.getRouteCoordinates(item);
          return coord.lat === location.lat && coord.lng === location.lng;
        });
        return isAlong.length > 0;
      });

      return alongPlaces.filter((item) => {
        const nextWaypoint = nextWaypoints[index];

        const nextLocation = Utils.Places.getRouteCoordinates(
          nextWaypoint.place
        );
        const currentLocation = Utils.Places.getRouteCoordinates(item);

        return !Utils.Coordinates.isEqualLatLng(nextLocation, currentLocation);
      });
    });

    const distancesToNextWaypoints = includedBestBikingRoads.map(
      (item, index) => {
        const nextWaypoint = nextWaypoints[index];

        const coordsWithDirection = item.getDirection(currentWaypoint);
        const lastCoord = coordsWithDirection[coordsWithDirection.length - 1];

        return distanceTo(nextWaypoint.waypoint, lastCoord);
      }
    );

    const points = includedBestBikingRoads.map((item, index) => {
      let rating = item.rating;

      const includedPlacesCount = placesAlong[index].length;
      rating += includedPlacesCount * 2;

      const currentDistanceToWaypoint = distancesToNextWaypoints[index];
      const minDistance = Math.min(...distancesToNextWaypoints);
      rating += currentDistanceToWaypoint === minDistance ? 1 : 0;

      return rating;
    });

    const selectedRoadIndex = includedBestBikingRoads.findIndex((_, index) => {
      const maxPoint = Math.max(...points);
      const currentPoint = points[index];
      return currentPoint === maxPoint;
    });

    const selectedRoad = includedBestBikingRoads.at(selectedRoadIndex);

    if (!selectedRoad) {
      updatedPlaces.push(currentPlace);
      updatedWaypoints.push(currentWaypoint);
      continue;
    }

    selectedBestBikingRoads.push(selectedRoad);
    usedBestBikingRoadsIDs.push(selectedRoad.id);

    // If next waypoint === current, it means road is in opposite direction.
    // We need to swap coordinates;

    croppedWaypoints[selectedRoadIndex].forEach((item) =>
      croppedWaypointsFrom.push(item)
    );

    const nextWaypoint = nextWaypoints[selectedRoadIndex];
    const { lat: nextLat, lng: nextLng } = nextWaypoint.waypoint;
    const isNextWayointIsCurrent =
      nextLat === currentWaypoint.lat && nextLng === currentWaypoint.lng;

    const directionCoordinates = isNextWayointIsCurrent
      ? [...selectedRoad.getDirection(currentWaypoint)].reverse()
      : selectedRoad.getDirection(currentWaypoint);

    const percentIndex = (directionCoordinates.length * 0.6).toFixed(0);

    const roadCoordinates = Utils.Coordinates.sortByDistance(
      directionCoordinates[0],
      directionCoordinates[directionCoordinates.length - 1],
      [
        directionCoordinates[Number(percentIndex)],
        ...placesAlong[selectedRoadIndex].map((i) =>
          Utils.Places.getRouteCoordinates(i)
        ),
      ]
    );

    /* Check if we don't exceed waypoints limit */

    const isLimitExceeded =
      selectedRoadsCoordinates.flat(1).length + roadCoordinates.length >=
      waypointsCountLimit;

    if (!isLimitExceeded) {
      selectedRoadsCoordinates.push(roadCoordinates);

      if (isNextWayointIsCurrent) {
        roadCoordinates.forEach((item) => {
          updatedWaypoints.push(item);
          updatedPlaces.push(
            routeWaypoints.availablePlaces.find((i) =>
              Utils.Coordinates.isEqualLatLng(
                Utils.Places.getRouteCoordinates(i),
                item
              )
            )
          );
        });
        updatedPlaces.push(currentPlace);
        updatedWaypoints.push(currentWaypoint);
      } else {
        updatedPlaces.push(currentPlace);
        updatedWaypoints.push(currentWaypoint);
        roadCoordinates.forEach((item) => {
          updatedWaypoints.push(item);
          updatedPlaces.push(
            routeWaypoints.availablePlaces.find((i) =>
              Utils.Coordinates.isEqualLatLng(
                Utils.Places.getRouteCoordinates(i),
                item
              )
            )
          );
        });
      }
    }
  }

  const buildRoutePayload = {
    avoid,
    optimize: false,
    withAlternativeRoads: false,
  };

  console.log("BUILD_BBK_ROUTE.TS", { updatedWaypoints });
  let response = await api.graphhopper.buildRoute({
    ...buildRoutePayload,
    points: convertToPoints(updatedWaypoints),
  });

  let newRouteWaypoints = new RouteWayponts({
    coordinates:
      response.paths[0].snapped_waypoints.coordinates.map(convertToLatLng),
    places: updatedPlaces,
  });

  return Promise.resolve({
    roads,
    path: response.paths[0],
    selectedBestBikingRoads,
    places: newRouteWaypoints.places,
    waypoints: newRouteWaypoints.waypoints,
  });
};
